import React from 'react';
import { useTranslation } from 'react-i18next';

import { RichTextEditor, Spinner } from '../../../shared/components';
import {
  ConversationEmailOptions,
  EmailComposeOptions,
  EmailComposePreview,
  EmailComposeSnippet,
  fillSnippetVariables,
  getEmailComposeOptions,
  htmlToPlainText,
  plainTextToHtml,
  previewEmailCompose,
} from '../services/emailComposeService';
import detailsStyles from './ConversationDetails.module.css';
import EmailSignatureEditor from './EmailSignatureEditor';
import styles from './EmailComposeModal.module.css';

const PREVIEW_DEBOUNCE_MS = 600;
const SUBJECT_MAX_LENGTH = 250;

interface EmailComposeModalProps {
  conversationId: string;
  isOpen: boolean;
  /** Plain composer text carried into the rich editor when the modal opens. */
  initialText: string;
  contactName?: string;
  agentName?: string;
  onClose: () => void;
  onSend: (content: string, email: ConversationEmailOptions) => Promise<void>;
}

const EmailComposeModal: React.FC<EmailComposeModalProps> = ({
  conversationId,
  isOpen,
  initialText,
  contactName,
  agentName,
  onClose,
  onSend,
}) => {
  const { t } = useTranslation(['conversations', 'common']);

  const [options, setOptions] = React.useState<EmailComposeOptions | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [subject, setSubject] = React.useState('');
  const [layoutId, setLayoutId] = React.useState('');
  const [html, setHtml] = React.useState('');
  const [usedSnippetIds, setUsedSnippetIds] = React.useState<string[]>([]);
  const [snippetMenuOpen, setSnippetMenuOpen] = React.useState(false);
  const [snippetSearch, setSnippetSearch] = React.useState('');
  const [insertRequest, setInsertRequest] = React.useState<{ id: number; html: string } | null>(null);
  const [showPreview, setShowPreview] = React.useState(true);
  const [preview, setPreview] = React.useState<EmailComposePreview | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const [editingSignature, setEditingSignature] = React.useState(false);
  const previewRequestRef = React.useRef(0);
  const initialTextRef = React.useRef(initialText);
  initialTextRef.current = initialText;

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;
    setOptions(null);
    setLoadError(null);
    setSendError(null);
    setPreview(null);
    setUsedSnippetIds([]);
    setSnippetMenuOpen(false);
    setSnippetSearch('');
    setInsertRequest(null);
    setEditingSignature(false);
    setHtml(plainTextToHtml(initialTextRef.current));

    getEmailComposeOptions(conversationId)
      .then(result => {
        if (cancelled) {
          return;
        }
        setOptions(result);
        setSubject(result.defaultSubject);
        setLayoutId('');
      })
      .catch(error => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : t('emailComposeLoadFailed'));
        }
      });

    return () => { cancelled = true; };
  }, [conversationId, isOpen, t]);

  const plainText = React.useMemo(() => htmlToPlainText(html), [html]);

  React.useEffect(() => {
    if (!isOpen || !options || !showPreview) {
      return undefined;
    }

    const requestId = ++previewRequestRef.current;
    setPreviewLoading(true);
    const timer = window.setTimeout(() => {
      previewEmailCompose(conversationId, { subject, htmlBody: html, text: plainText, layoutId: layoutId || undefined })
        .then(result => {
          if (previewRequestRef.current === requestId) {
            setPreview(result);
          }
        })
        .catch(() => {
          if (previewRequestRef.current === requestId) {
            setPreview(null);
          }
        })
        .finally(() => {
          if (previewRequestRef.current === requestId) {
            setPreviewLoading(false);
          }
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [conversationId, html, isOpen, layoutId, options, plainText, showPreview, subject]);

  const filteredSnippets = React.useMemo(() => {
    const snippets = options?.snippets ?? [];
    const query = snippetSearch.trim().toLowerCase().replace(/^\//, '');
    if (!query) {
      return snippets;
    }
    return snippets.filter(snippet =>
      snippet.name.toLowerCase().includes(query)
      || snippet.shortcut?.includes(query)
      || snippet.category?.toLowerCase().includes(query));
  }, [options?.snippets, snippetSearch]);

  const insertSnippet = (snippet: EmailComposeSnippet) => {
    const filled = fillSnippetVariables(snippet.bodyHtml, {
      contactName,
      agentName,
      companyName: options?.companyName,
    });
    setInsertRequest(prev => ({ id: (prev?.id ?? 0) + 1, html: filled }));
    setUsedSnippetIds(prev => (prev.includes(snippet.id) ? prev : [...prev, snippet.id]));
    setSnippetMenuOpen(false);
    setSnippetSearch('');
  };

  const isTextOnly = options?.replyFormat === 'text';
  const canSubmit = Boolean(options?.canSend) && plainText.length > 0 && !sending;

  const submit = async () => {
    if (!canSubmit) {
      return;
    }

    setSending(true);
    setSendError(null);
    try {
      await onSend(plainText, {
        subject: subject.trim() || undefined,
        htmlBody: isTextOnly ? undefined : html,
        layoutId: layoutId || undefined,
        snippetIds: usedSnippetIds.length > 0 ? usedSnippetIds : undefined,
      });
      onClose();
    } catch (error) {
      setSendError(error instanceof Error ? error.message : t('failedToSendMessage', { ns: 'conversations' }));
    } finally {
      setSending(false);
    }
  };

  const requestClose = () => {
    if (sending) {
      return;
    }
    if (plainText && plainText !== initialText.trim() && !window.confirm(t('emailComposeDiscardConfirm'))) {
      return;
    }
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  const mailboxLayoutName = options?.layouts.find(layout => layout.id === options.mailboxLayoutId)?.name
    ?? options?.layouts.find(layout => layout.isDefault)?.name;

  return (
    <div className={detailsStyles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('emailComposeTitle')}>
      <div className={`${detailsStyles.modalCard} ${styles.card}`}>
        <div className={detailsStyles.modalHeader}>
          <div>
            <div className={detailsStyles.modalTitle}>{t('emailComposeTitle')}</div>
            {options?.recipient ? (
              <div className={styles.subtitle}>{t('emailComposeTo', { address: options.recipient })}</div>
            ) : null}
          </div>
          <button type="button" className={detailsStyles.modalCloseBtn} onClick={requestClose} disabled={sending}>
            {t('close', { ns: 'common' })}
          </button>
        </div>

        {loadError ? <div className={detailsStyles.modalError}>{loadError}</div> : null}
        {!options && !loadError ? <div className={styles.loading}><Spinner /></div> : null}

        {options ? (
          <div className={`${styles.body} ${showPreview ? styles.bodyWithPreview : ''}`}>
            <div className={styles.editor}>
              {!options.canSend ? (
                <div className={detailsStyles.modalError}>{t('emailComposeCannotSend')}</div>
              ) : null}

              <label className={styles.field}>
                <span>{t('emailComposeSubject')}</span>
                <input
                  className="text-input"
                  value={subject}
                  onChange={event => setSubject(event.target.value)}
                  maxLength={SUBJECT_MAX_LENGTH}
                  disabled={sending}
                />
              </label>

              <div className={styles.row}>
                <label className={styles.field}>
                  <span>{t('emailComposeLayout')}</span>
                  <select
                    className="text-input"
                    value={layoutId}
                    onChange={event => setLayoutId(event.target.value)}
                    disabled={sending || isTextOnly}
                    aria-label={t('emailComposeLayout')}
                  >
                    <option value="">
                      {mailboxLayoutName
                        ? t('emailComposeLayoutMailboxDefaultNamed', { name: mailboxLayoutName })
                        : t('emailComposeLayoutMailboxDefault')}
                    </option>
                    {options.layouts.map(layout => (
                      <option key={layout.id} value={layout.id}>{layout.name}</option>
                    ))}
                  </select>
                </label>

                <div className={styles.snippetWrap}>
                  <button
                    type="button"
                    className={detailsStyles.modalSecondaryBtn}
                    onClick={() => setSnippetMenuOpen(current => !current)}
                    disabled={sending || options.snippets.length === 0}
                    aria-expanded={snippetMenuOpen}
                    title={options.snippets.length === 0 ? t('emailComposeNoSnippets') : undefined}
                  >
                    {t('emailComposeInsertSnippet')}
                  </button>
                  {snippetMenuOpen ? (
                    <div className={styles.snippetMenu} role="listbox" aria-label={t('emailComposeInsertSnippet')}>
                      <input
                        className="text-input"
                        type="search"
                        value={snippetSearch}
                        onChange={event => setSnippetSearch(event.target.value)}
                        placeholder={t('emailComposeSnippetSearch')}
                        aria-label={t('emailComposeSnippetSearch')}
                        autoFocus
                        onKeyDown={event => {
                          if (event.key === 'Escape') {
                            setSnippetMenuOpen(false);
                          } else if (event.key === 'Enter' && filteredSnippets[0]) {
                            event.preventDefault();
                            insertSnippet(filteredSnippets[0]);
                          }
                        }}
                      />
                      <div className={styles.snippetList}>
                        {filteredSnippets.map(snippet => (
                          <button
                            key={snippet.id}
                            type="button"
                            role="option"
                            aria-selected={false}
                            className={styles.snippetItem}
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => insertSnippet(snippet)}
                          >
                            <span className={styles.snippetName}>{snippet.name}</span>
                            <span className={styles.snippetMeta}>
                              {[snippet.shortcut ? `/${snippet.shortcut}` : null, snippet.category].filter(Boolean).join(' · ')}
                            </span>
                          </button>
                        ))}
                        {filteredSnippets.length === 0 ? (
                          <div className={styles.snippetEmpty}>{t('emailComposeNoSnippetMatches')}</div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {isTextOnly ? <p className={styles.hint}>{t('emailComposeTextOnlyHint')}</p> : null}

              <RichTextEditor
                value={html}
                onChange={setHtml}
                minHeight={220}
                placeholder={t('emailComposePlaceholder')}
                ariaLabel={t('emailComposeBody')}
                insertRequest={insertRequest}
              />
              {editingSignature ? (
                <EmailSignatureEditor
                  onCancel={() => setEditingSignature(false)}
                  onSaved={signature => {
                    setOptions(prev => (prev ? { ...prev, agentSignatureHtml: signature.previewHtml || null } : prev));
                    setEditingSignature(false);
                  }}
                />
              ) : (
                <div className={styles.signatureBar}>
                  {options.agentSignaturesEnabled && options.agentSignatureHtml ? (
                    <span className={styles.hint}>{t('emailComposeAgentSignatureHint')}</span>
                  ) : options.hasSignature ? (
                    <span className={styles.hint}>{t('emailComposeSignatureHint')}</span>
                  ) : null}
                  {options.agentSignaturesEnabled ? (
                    <button type="button" className={styles.linkBtn} onClick={() => setEditingSignature(true)} disabled={sending}>
                      {options.agentSignatureHtml ? t('emailComposeEditSignature') : t('emailComposeAddSignature')}
                    </button>
                  ) : null}
                </div>
              )}
            </div>

            {showPreview ? (
              <div className={styles.previewPane}>
                <div className={styles.previewHead}>
                  <span>{t('emailComposePreview')}</span>
                  {previewLoading ? <span className={styles.hint}>{t('emailComposePreviewUpdating')}</span> : null}
                </div>
                {preview ? (
                  <iframe
                    title={t('emailComposePreview')}
                    className={styles.previewFrame}
                    sandbox=""
                    srcDoc={preview.html}
                    data-testid="email-compose-preview"
                  />
                ) : <div className={styles.loading}><Spinner /></div>}
                {preview?.exceedsClipLimit ? (
                  <p className={styles.warn}>{t('emailComposeTooLarge', { size: Math.round(preview.htmlBytes / 1024) })}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {sendError ? <div className={detailsStyles.modalError}>{sendError}</div> : null}

        <div className={`${detailsStyles.modalActions} ${styles.actions}`}>
          <button
            type="button"
            className={detailsStyles.modalSecondaryBtn}
            onClick={() => setShowPreview(current => !current)}
            disabled={!options}
            aria-pressed={showPreview}
          >
            {showPreview ? t('emailComposeHidePreview') : t('emailComposeShowPreview')}
          </button>
          <span className={styles.spacer} />
          <button type="button" className={detailsStyles.modalSecondaryBtn} onClick={requestClose} disabled={sending}>
            {t('cancel', { ns: 'common' })}
          </button>
          <button type="button" className={detailsStyles.modalPrimaryBtn} onClick={() => { void submit(); }} disabled={!canSubmit}>
            {sending ? t('emailComposeSending') : t('emailComposeSend')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposeModal;
